/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable no-console */
/**
 * Subscription Modal component
 *
 * Allows users to manage report subscriptions for single or multiple users
 * Supports both individual and bulk subscription management
 */
import ModalDialog from 'components/ModalDialog';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import callApi from 'lib/callApi';
import { showFailureToast, showSuccessToast } from 'lib/ToastHelper';
import React, { useEffect, useState } from 'react';
import { Button, Card, CardBody, Badge, Alert } from 'reactstrap';
import { companyIdAtom } from 'views/state';

import {
  membersGridDataRefreshAtom,
  selectedReportTypesAtom,
  selectedRowIdsAtom,
} from './state';
import SubscriptionModalDataGrid from './SubscriptionModalDataGrid';

interface SubscriptionModalProps {
  isOpen: boolean;
  toggle: () => void;
  userId?: string;
  isBulk?: boolean;
  userSubscriptions?: Record<
    string,
    { report_type_id: number; subscribed: boolean }
  >;
}

/**
 * Modal component for managing user subscriptions to reports
 * Supports both single user and bulk management
 */
const SubscriptionModal: React.FC<SubscriptionModalProps> = ({
  isOpen,
  toggle,
  userId,
  isBulk = false,
  userSubscriptions = {},
}) => {
  const companyId = useAtomValue(companyIdAtom);
  const selectedUserIds = useAtomValue(selectedRowIdsAtom);
  const [selectedReportTypes, setSelectedReportTypes] = useAtom(
    selectedReportTypesAtom,
  );
  const refreshMembersGridAtom = useSetAtom(membersGridDataRefreshAtom);

  const [submitting, setSubmitting] = useState(false);

  // Reset selected report types when modal closes
  useEffect(() => {
    setSelectedReportTypes([]);
  }, [isOpen, isBulk, setSelectedReportTypes]);

  // Handle subscription (Single User or Bulk)
  const handleSubmit = async (submitAction: 'subscribe' | 'unsubscribe') => {
    if (selectedReportTypes.length === 0) {
      showFailureToast('Please select at least one report type');
      return;
    }

    setSubmitting(true);

    try {
      // Prepare API data
      const data = {
        company_id: companyId,
        user_ids:
          isBulk && selectedUserIds.length
            ? selectedUserIds.join(',')
            : String(userId),
        report_types: selectedReportTypes.join(','),
        subscribed: submitAction === 'subscribe',
      };

      await callApi(`/companies/manage-subscribe/`, 'PATCH', {}, data);

      // refresh members listings
      refreshMembersGridAtom(Math.random().toString());
      showSuccessToast(
        `Successfully ${
          submitAction === 'subscribe' ? 'subscribed to' : 'unsubscribed from'
        } selected report types`,
      );
    } catch (err) {
      showFailureToast(
        `Failed to ${
          submitAction === 'subscribe' ? 'subscribe to' : 'unsubscribe from'
        } reports`,
      );
      console.error('Subscription update error:', err);
    } finally {
      setSubmitting(false);
    }
  };

  // Get modal title based on mode
  const getModalTitle = () => {
    if (isBulk) {
      return 'Bulk Subscription Management';
    }
    return `Manage Report Subscriptions for User`;
  };

  // Render current subscription details
  const getCurrentSubscriptionDetails = () => {
    const hasSubscriptions =
      userSubscriptions &&
      Object.keys(userSubscriptions).some(
        (key) => userSubscriptions[key]?.subscribed,
      );

    if (!hasSubscriptions) {
      return (
        <Alert color="primary" className="mt-3">
          <i className="fa fa-info-circle mr-2"></i>
          This user is not subscribed to any reports. Select report types below
          to subscribe.
        </Alert>
      );
    }
    return null;
  };

  // Render action buttons for subscription management
  const renderActionButtons = () => {
    if (selectedReportTypes.length === 0) return null;

    // In bulk mode, always show both buttons
    if (isBulk) {
      return (
        <div className="d-flex justify-content-between">
          <div className="mr-2">
            <Button
              color="secondary"
              onClick={() => handleSubmit('unsubscribe')}
              disabled={submitting}
            >
              Unsubscribe
            </Button>
          </div>
          <div>
            <Button
              color="primary"
              onClick={() => handleSubmit('subscribe')}
              disabled={submitting}
            >
              Subscribe
            </Button>
          </div>
        </div>
      );
    }

    // For individual user mode, determine which buttons to show based on the status of selected reports
    const selectedReportsData = selectedReportTypes.map((reportId) => {
      const subscriptionEntry = Object.values(userSubscriptions).find(
        (sub) => sub?.report_type_id === reportId,
      );
      return { subscribed: Boolean(subscriptionEntry?.subscribed) };
    });

    const hasSubscribedReports = selectedReportsData.some(
      (report) => report.subscribed,
    );
    const hasUnsubscribedReports = selectedReportsData.some(
      (report) => !report.subscribed,
    );

    return (
      <div className="d-flex justify-content-between">
        {hasSubscribedReports && (
          <div className="mr-2">
            <Button
              color="secondary"
              onClick={() => handleSubmit('unsubscribe')}
              disabled={submitting}
            >
              Unsubscribe
            </Button>
          </div>
        )}
        {hasUnsubscribedReports && (
          <div>
            <Button
              color="primary"
              onClick={() => handleSubmit('subscribe')}
              disabled={submitting}
            >
              Subscribe
            </Button>
          </div>
        )}
      </div>
    );
  };

  // Render bulk mode information
  const renderBulkModeInfo = () => {
    if (!isBulk) return null;

    return (
      <div className="d-flex flex-column justify-content-between">
        <div className="d-flex align-items-center mb-2">
          <i className="fa fa-users fa-lg mr-2 text-primary"></i>
          <h6 className="mb-0">Bulk Management</h6>
          <Badge color="primary" pill className="ml-2">
            {selectedUserIds.length} users selected
          </Badge>
        </div>

        <div
          style={{
            backgroundColor: 'rgba(255, 193, 7, 0.25)', // Adaptive warning background
            border: '1px solid rgba(255, 193, 7, 0.6)', // Subtle border
            borderRadius: '5px',
            padding: '12px',
          }}
        >
          <p
            style={{
              display: 'flex',
              alignItems: 'center',
              fontWeight: 'bold',
            }}
          >
            <i className="fa fa-exclamation-triangle mr-2"></i>
            Subscription Changes Will Override Current Settings
          </p>
          <p>
            Any changes made here will affect all selected users and override
            their current subscription settings.
          </p>
        </div>
      </div>
    );
  };

  return isOpen ? (
    <ModalDialog
      title={
        <div>
          <i className="fa fa-bell mr-2"></i>
          {getModalTitle()}
        </div>
      }
      childComponent={
        <>
          {isBulk && renderBulkModeInfo()}

          <Card>
            <CardBody>
              {!isBulk && getCurrentSubscriptionDetails()}

              <div>
                <div className="d-flex justify-content-between align-items-center p-3">
                  <div className="d-flex align-items-center">
                    <i className="fa fa-file-alt mr-2 text-primary"></i>
                    <h6 className="font-weight-bold mb-0">
                      Select Report Types
                    </h6>
                  </div>
                </div>

                <SubscriptionModalDataGrid
                  subscriptions={userSubscriptions}
                  isBulk={isBulk}
                />

                <p
                  style={{
                    marginBottom: '0',
                    visibility: `${
                      selectedReportTypes.length > 0 ? 'visible' : 'hidden'
                    }`,
                  }}
                >
                  <i className="fa fa-check-circle mr-1 text-success"></i>
                  {selectedReportTypes.length} report type
                  {selectedReportTypes.length > 1 ? 's' : ''} selected
                </p>
              </div>

              <div style={{ color: 'dodgerblue' }}>
                <i className="fa fa-info-circle mr-1"></i>
                Changes take effect with the next report generation cycle
              </div>
            </CardBody>
          </Card>
        </>
      }
      onClose={() => {
        toggle();
      }}
      customFooter={
        <>
          <div>
            <Button color="secondary" onClick={toggle} disabled={submitting}>
              Close
            </Button>
          </div>
          <div className="ml-auto">{renderActionButtons()}</div>
        </>
      }
    />
  ) : null;
};

export default SubscriptionModal;
